import React, { useEffect, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowDownToLine, CreditCard, Wallet, LogOut, ChevronRight, Copy, Check } from 'lucide-react';
import MenuClient from '../Components/menuclient';

const fmtMoney = (n, dev = 'EUR') =>
  `${Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${dev}`;

const initials = (u) =>
  ((`${u?.prenom ?? ''} ${u?.nom ?? ''}`.trim() || u?.name || '?')
    .split(' ')
    .map((s) => s.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase());

const fullName = (u) =>
  (`${u?.prenom ?? ''} ${u?.nom ?? ''}`.trim()) || u?.name || 'Utilisateur';

export default function MonCompte() {
  const history = useHistory();
  const [copiedField, setCopiedField] = React.useState(null);

  const session = useMemo(() => {
    try {
      const raw = localStorage.getItem('bank.session');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const token = useMemo(() => {
    try {
      return localStorage.getItem('bank.token') || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!session || !session.user || !token) {
      history.replace('/banque/login');
    }
  }, [session, token, history]);

  if (!session || !session.user || !token) return null;

  const user = session.user || {};
  const account = session.account || {};
  const card = session.card || null;

  const name = fullName(user);
  const adresse = user?.adresse || 'France';
  const email = user?.email || '—';
  const solde = account?.solde ?? 0;
  const devise = account?.devise || 'EUR';
  const actif = !!account?.active;
  const iban = account?.iban || '—';
  const ibanFormatted = iban !== '—' ? iban.match(/.{1,4}/g)?.join(' ') : '—';

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Erreur de copie:', err);
    }
  };

  const handleLogout = () => {
  const confirmed = window.confirm("Êtes-vous sûr de vouloir vous déconnecter ?");

  if (confirmed) {
    const keysToRemove = [
      'bank.session',
      'bank.token',
      'bank.user',
      'bank.account',
      'bank.card',
      'bank.role',
      'bank.iban',
      'bank.iban_formatted',
      'bank.iban_masked',
    ];

    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // 🔁 Redirection vers la page de connexion
    history.push('/banque/login');
  }
};


  return (
    <div style={styles.page}>
      {/* Hero Section avec profil */}
      <motion.div 
        style={styles.heroSection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div style={styles.heroContent}>
          <motion.div 
            style={styles.headerTop}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div style={styles.logoContainer}>
              <Wallet size={28} strokeWidth={2.5} />
              <span style={styles.bankName}>Mon Compte</span>
            </div>
            <button
              onClick={handleLogout}
              style={styles.logoutButton}
              title="Se déconnecter"
            >
              <LogOut size={20} />
            </button>
          </motion.div>

          <motion.div
            style={styles.profileSection}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 100 }}
          >
            <div style={styles.avatar}>{initials(user)}</div>
            <div style={styles.profileInfo}>
              <div style={styles.profileName}>{name}</div>
              <div style={styles.profileEmail}>{email}</div>
            </div>
            <span
              style={{
                ...styles.statusBadge,
                backgroundColor: actif ? '#16A34A' : '#6B7280',
              }}
            >
              {actif ? '✓ Actif' : '✗ Inactif'}
            </span>
          </motion.div>

          <motion.div
            style={styles.balanceCard}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div style={styles.balanceLabel}>Solde disponible</div>
            <div style={styles.balanceAmount}>{fmtMoney(solde, devise)}</div>
          </motion.div>
        </div>
      </motion.div>

      {/* Actions rapides */}
      <motion.div 
        style={styles.quickActions}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <Link to="/banque/virement" style={styles.actionLink}>
          <motion.button 
            style={styles.actionButton}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div style={styles.actionIcon}>
              <ArrowDownToLine size={24} />
            </div>
            <span style={styles.actionText}>Virement</span>
          </motion.button>
        </Link>

        <Link to="/banque/carte" style={styles.actionLink}>
          <motion.button 
            style={styles.actionButton}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div style={styles.actionIcon}>
              <CreditCard size={24} />
            </div>
            <span style={styles.actionText}>Ma carte</span>
          </motion.button>
        </Link>
      </motion.div>

      {/* Section Informations détaillées */}
      <motion.div 
        style={styles.detailsSection}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <h3 style={styles.sectionTitle}>Informations du compte</h3>

        <div style={styles.detailCard}>
          <motion.div 
            style={styles.detailRow}
            whileHover={{ backgroundColor: '#F9FAFB' }}
          >
            <div style={styles.detailLeft}>
              <div style={styles.detailLabel}>Titulaire</div>
              <div style={styles.detailValue}>{name}</div>
            </div>
            <ChevronRight size={18} color="#D1D5DB" />
          </motion.div>

          <motion.div 
            style={styles.detailRow}
            whileHover={{ backgroundColor: '#F9FAFB' }}
          >
            <div style={styles.detailLeft}>
              <div style={styles.detailLabel}>Adresse</div>
              <div style={styles.detailValue}>{adresse}</div>
            </div>
            <ChevronRight size={18} color="#D1D5DB" />
          </motion.div>

          <motion.div 
            style={styles.detailRow}
            whileHover={{ backgroundColor: '#F9FAFB' }}
          >
            <div style={styles.detailLeft}>
              <div style={styles.detailLabel}>Email</div>
              <div style={styles.detailValue}>{email}</div>
            </div>
            <button
              onClick={() => handleCopy(email, 'email')}
              style={styles.copyButton}
              title="Copier"
            >
              {copiedField === 'email' ? <Check size={16} color="#16A34A" /> : <Copy size={16} color="#9CA3AF" />}
            </button>
          </motion.div>

          <motion.div 
            style={styles.detailRow}
            whileHover={{ backgroundColor: '#F9FAFB' }}
          >
            <div style={styles.detailLeft}>
              <div style={styles.detailLabel}>IBAN</div>
              <div style={{...styles.detailValue, fontFamily: 'monospace', fontSize: '13px'}}>
                {ibanFormatted}
              </div>
            </div>
            <button
              onClick={() => handleCopy(iban, 'iban')}
              style={styles.copyButton}
              title="Copier"
            >
              {copiedField === 'iban' ? <Check size={16} color="#16A34A" /> : <Copy size={16} color="#9CA3AF" />}
            </button>
          </motion.div>

          <motion.div 
            style={styles.detailRow}
            whileHover={{ backgroundColor: '#F9FAFB' }}
          >
            <div style={styles.detailLeft}>
              <div style={styles.detailLabel}>Devise</div>
              <div style={styles.detailValue}>{devise}</div>
            </div>
            <ChevronRight size={18} color="#D1D5DB" />
          </motion.div>

          <motion.div 
            style={{...styles.detailRow, borderBottom: 'none'}}
            whileHover={{ backgroundColor: '#F9FAFB' }}
          >
            <div style={styles.detailLeft}>
              <div style={styles.detailLabel}>Statut</div>
              <div style={{
                ...styles.detailValue,
                color: actif ? '#16A34A' : '#DC2626',
                fontWeight: '700'
              }}>
                {actif ? 'Compte actif' : 'Compte inactif'}
              </div>
            </div>
            <ChevronRight size={18} color="#D1D5DB" />
          </motion.div>
        </div>

        <div style={styles.hintBox}>
          <p style={styles.hintText}>
            💳 Pour consulter le numéro complet de votre carte virtuelle, rendez-vous sur la page{' '}
            <Link to="/banque/carte" style={styles.hintLink}>Carte virtuelle</Link>.
          </p>
        </div>
      </motion.div>

      <MenuClient />
      <div style={{ height: '100px' }}></div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#F3F4F6',
    paddingBottom: '80px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  heroSection: {
    background: 'linear-gradient(135deg, #0061A6 0%, #003D6B 100%)',
    color: 'white',
    padding: '24px 20px 32px',
    borderBottomLeftRadius: '24px',
    borderBottomRightRadius: '24px',
    boxShadow: '0 4px 20px rgba(0, 97, 166, 0.3)',
  },
  heroContent: {
    maxWidth: '480px',
    margin: '0 auto',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  bankName: {
    fontSize: '20px',
    fontWeight: '700',
    letterSpacing: '-0.5px',
  },
  logoutButton: {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(10px)',
    border: 'none',
    borderRadius: '12px',
    padding: '10px',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
  },
  profileSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
    padding: '16px',
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
  },
  avatar: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '800',
    fontSize: '20px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: '18px',
    fontWeight: '700',
    marginBottom: '4px',
  },
  profileEmail: {
    fontSize: '13px',
    opacity: 0.85,
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
  },
  balanceCard: {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '24px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    textAlign: 'center',
  },
  balanceLabel: {
    fontSize: '13px',
    opacity: 0.85,
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '500',
  },
  balanceAmount: {
    fontSize: '42px',
    fontWeight: '700',
    letterSpacing: '-1px',
  },
  quickActions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    padding: '20px',
    maxWidth: '480px',
    margin: '0 auto',
    marginTop: '-20px',
  },
  actionLink: {
    textDecoration: 'none',
  },
  actionButton: {
    width: '100%',
    background: 'white',
    border: 'none',
    borderRadius: '16px',
    padding: '20px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
  },
  actionIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #0061A6 0%, #003D6B 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
  },
  actionText: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#111827',
  },
  detailsSection: {
    padding: '20px',
    maxWidth: '480px',
    margin: '0 auto',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '16px',
  },
  detailCard: {
    background: 'white',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid #F3F4F6',
    transition: 'background-color 0.2s ease',
  },
  detailLeft: {
    flex: 1,
  },
  detailLabel: {
    fontSize: '12px',
    color: '#6B7280',
    marginBottom: '4px',
    fontWeight: '500',
  },
  detailValue: {
    fontSize: '15px',
    color: '#111827',
    fontWeight: '600',
  },
  copyButton: {
    background: 'transparent',
    border: 'none',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    transition: 'background-color 0.2s ease',
  },
  hintBox: {
    marginTop: '16px',
    padding: '16px',
    background: 'white',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
  },
  hintText: {
    margin: 0,
    fontSize: '13px',
    color: '#6B7280',
    lineHeight: '1.6',
  },
  hintLink: {
    color: '#0061A6',
    textDecoration: 'none',
    fontWeight: '600',
  },
};